import type { OptinTemplate } from "@/types/optins";

import { optinStatus } from "@/utils/optinStatus";

export const EXCHANGE_TYPES = [
  "overlay",
  "fullPage",
  "topBar",
  "bottomTray",
  "bottomBar",
  "embed",
] as const;

export type ExchangeType = (typeof EXCHANGE_TYPES)[number];

export const TEMPLATE_MEDIUMS = ["email", "sms", "email_sms"] as const;

export type TemplateMedium = (typeof TEMPLATE_MEDIUMS)[number];

export const EXCHANGE_TYPE_LABELS: Record<ExchangeType, string> = {
  overlay: "Overlay",
  fullPage: "Full page",
  topBar: "Top bar",
  bottomTray: "Bottom tray",
  bottomBar: "Bottom bar",
  embed: "Embed",
};

const TEMPLATE_MEDIUM_LABELS: Record<TemplateMedium, string> = {
  email: "Email",
  sms: "Text",
  email_sms: "Email + Text",
};

/** Mirrors the API's derived optin status. */
export type Status = "active" | "scheduled" | "inactive";

export const STATUS_TYPES = ["active", "scheduled", "inactive"] as const;

/** "inactive" reads as Draft, matching the card chip and the broadcast list. */
const STATUS_LABELS: Record<Status, string> = {
  active: "Active",
  scheduled: "Scheduled",
  inactive: "Draft",
};

export function getStatusLabel(status: Status): string {
  return STATUS_LABELS[status];
}

export function getExchangeTypeLabel(type: ExchangeType): string {
  return EXCHANGE_TYPE_LABELS[type];
}

export function getTemplateMediumLabel(medium: TemplateMedium): string {
  return TEMPLATE_MEDIUM_LABELS[medium];
}

export function getExchangeType(template: OptinTemplate): ExchangeType {
  if (template.mode === "topbar") return "topBar";
  if (template.mode === "bottomtray") return "bottomTray";
  if (template.mode === "bottombar") return "bottomBar";
  if (template.mode === "embedded") return "embed";
  if (template.isFullScreen === true) return "fullPage";
  return "overlay";
}

function stepCollectsEmail(html: string): boolean {
  return /type=["']email["']/i.test(html);
}

function stepCollectsPhone(html: string): boolean {
  return /type=["']tel["']/i.test(html);
}

export function getTemplateMedium(template: OptinTemplate): TemplateMedium {
  const hasEmail = template.steps.some((step) => stepCollectsEmail(step.html));
  const hasPhone = template.steps.some((step) => stepCollectsPhone(step.html));

  if (hasEmail && hasPhone) return "email_sms";
  if (hasPhone) return "sms";
  return "email";
}

export function isVisibleExchangeTemplate(template: OptinTemplate): boolean {
  return template.mode !== "noContent";
}

export type ExchangeTemplateFilters = {
  medium?: TemplateMedium;
  exchangeType?: ExchangeType;
  status?: Status;
  search?: string;
};

export function filterExchangeTemplates(
  templates: OptinTemplate[],
  filters: ExchangeTemplateFilters,
): OptinTemplate[] {
  const search = filters.search?.trim().toLowerCase();

  return templates.filter((template) => {
    if (!isVisibleExchangeTemplate(template)) return false;

    if (filters.medium && getTemplateMedium(template) !== filters.medium) {
      return false;
    }

    if (
      filters.exchangeType &&
      getExchangeType(template) !== filters.exchangeType
    ) {
      return false;
    }

    // Compared against the derived status, so "Scheduled" is selectable and
    // a spent window counts as Draft rather than Active.
    if (filters.status && optinStatus(template) !== filters.status) {
      return false;
    }

    if (search && !template.name?.toLowerCase().includes(search)) {
      return false;
    }

    return true;
  });
}
